import { FreeTrialsLeft } from "@/types/freeTrialsLeft"
import { User } from "@/types/user"
import { ResourceUsageEntry, UserLimits } from "@/types/userLimits"
import { useQuery } from "@tanstack/react-query"
import apiClient from "./apiClient"

// A virtual limit (backend `virtual_limit`, e.g. auto_fill_runs) is a plain
// limit definition: nothing is counted for it, so the entry has no usage.
interface UserLimitApiEntry {
  resource: string
  limit: number
  period: string
  usage?: number | null
}

// API response after automatic snake_case → camelCase conversion by apiClient
interface UserLimitsApiResponse {
  resourceUsage: {
    questionnaires?: UserLimitApiEntry
    files?: UserLimitApiEntry
    chatInteractions?: UserLimitApiEntry
    extensionInteractions?: UserLimitApiEntry
    chatMessagesDaily?: UserLimitApiEntry
    aiCredits?: UserLimitApiEntry
  }
}

/** Only an entry with a limit and a usage can be shown as used/limit; without
 *  one it counts as no tracked limit, which the UI already hides. */
const hasUsage = (
  entry: UserLimitApiEntry | undefined,
): entry is ResourceUsageEntry =>
  entry !== undefined &&
  Number.isFinite(entry.limit) &&
  Number.isFinite(entry.usage)

const userApiCalls = {
  getUserData: async (): Promise<User> => {
    const response = await apiClient.get("/users/me")
    return response.data
  },
  // null for a user who is not on a trial (an upgraded one included)
  getUserFreeTrialsLeftData: async (): Promise<FreeTrialsLeft | null> => {
    const response = await apiClient.get("/users/me/trial")
    return response.data
  },
  getUsersLimits: async (): Promise<UserLimits> => {
    const response =
      await apiClient.get<UserLimitsApiResponse>("users/me/limits")

    // Transform snake_case API response to camelCase for frontend
    const apiData = response.data
    const usage = apiData.resourceUsage || {}
    const shown = (entry: UserLimitApiEntry | undefined) =>
      hasUsage(entry) ? entry : undefined
    // Transform snake_case keys to camelCase for frontend, and resource_usage to resourceUsage
    return {
      resourceUsage: {
        questionnaires: shown(usage.questionnaires),
        files: shown(usage.files),
        chatMessages: shown(usage.chatInteractions),
        chatMessagesDaily: shown(usage.chatMessagesDaily),
        extensionCalls: shown(usage.extensionInteractions),
        aiCredits: shown(usage.aiCredits),
      },
    }
  },
}

export const useGetUserData = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["userData"],
    queryFn: userApiCalls.getUserData,
    enabled,
  })
}

export const useGetUserFreeTrialsLeftData = () => {
  return useQuery({
    queryKey: ["userFreeTrialsLeftData"],
    queryFn: userApiCalls.getUserFreeTrialsLeftData,
  })
}

export const USER_LIMITS_QUERY_KEY = ["userLimits"] as const

export const useGetUsersLimits = () => {
  return useQuery({
    queryKey: USER_LIMITS_QUERY_KEY,
    queryFn: userApiCalls.getUsersLimits,
  })
}
